import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Paper,
  Container,
  CircularProgress,
  InputAdornment,
  IconButton,
  Link as MuiLink,
  useTheme,
  alpha,
  LinearProgress,
  Chip,
  Stack
} from '@mui/material';
import {
  Lock,
  Visibility,
  VisibilityOff,
  ArrowBack,
  CheckCircle,
  Cancel,
  Security
} from '@mui/icons-material';
import { styled, keyframes } from '@mui/material/styles';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

// Helper functie om runtime configuratie te gebruiken
const getCentralBackendURL = () => {
  // Runtime configuratie (Docker container)
  if (window.ENV_CONFIG && window.ENV_CONFIG.REACT_APP_CENTRAL_BACKEND_URL) {
    return window.ENV_CONFIG.REACT_APP_CENTRAL_BACKEND_URL;
  }
  // Build-time configuratie (fallback)
  return process.env.REACT_APP_CENTRAL_BACKEND_URL || 'http://localhost:8001';
};

// Keyframe animaties voor waterstof achtergrond (hergebruikt van Login.jsx)
const float = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  33% { transform: translateY(-20px) rotate(120deg); }
  66% { transform: translateY(10px) rotate(240deg); }
`;

const floatSlow = keyframes`
  0%, 100% { transform: translateY(0px) translateX(0px) scale(1); }
  50% { transform: translateY(-30px) translateX(15px) scale(1.1); }
`;

const bubble = keyframes`
  0% { transform: translateY(100vh) scale(0); opacity: 0; }
  10% { opacity: 1; }
  90% { opacity: 1; }
  100% { transform: translateY(-100px) scale(1); opacity: 0; }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.4; transform: scale(1); }
  50% { opacity: 0.8; transform: scale(1.05); }
`;

const gradientShift = keyframes`
  0%, 100% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
`;

// Styled components (consistent met Login.jsx)
const PageContainer = styled(Container)(({ theme }) => ({
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  overflow: 'hidden',
  background: `linear-gradient(-45deg, 
    ${alpha(theme.palette.primary.main, 0.1)}, 
    ${alpha(theme.palette.secondary.main, 0.15)}, 
    ${alpha('#E3F2FD', 0.8)}, 
    ${alpha('#F1F8E9', 0.6)}
  )`,
  backgroundSize: '400% 400%',
  animation: `${gradientShift} 15s ease infinite`,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23000000" fill-opacity="0.02"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
    opacity: 0.3
  }
}));

// Waterstof animatie componenten
const HydrogenMolecule = styled('div')(({ theme, size = 40, delay = 0, duration = 8 }) => ({
  position: 'absolute',
  width: size,
  height: size,
  animation: `${float} ${duration}s ease-in-out infinite`,
  animationDelay: `${delay}s`,
  '&::before, &::after': {
    content: '""',
    position: 'absolute',
    borderRadius: '50%',
    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.6)}, ${alpha(theme.palette.primary.light, 0.4)})`,
  },
  '&::before': {
    width: size * 0.4,
    height: size * 0.4,
    top: 0,
    left: 0,
    boxShadow: `0 0 ${size * 0.2}px ${alpha(theme.palette.primary.main, 0.3)}`,
  },
  '&::after': {
    width: size * 0.4,
    height: size * 0.4,
    bottom: 0,
    right: 0,
    boxShadow: `0 0 ${size * 0.2}px ${alpha(theme.palette.primary.main, 0.3)}`,
  }
}));

const FloatingParticle = styled('div')(({ theme, size = 20, delay = 0, duration = 12 }) => ({
  position: 'absolute',
  width: size,
  height: size,
  borderRadius: '50%',
  background: `radial-gradient(circle, ${alpha(theme.palette.secondary.main, 0.4)}, ${alpha(theme.palette.secondary.main, 0.1)})`,
  animation: `${floatSlow} ${duration}s ease-in-out infinite`,
  animationDelay: `${delay}s`,
  boxShadow: `0 0 ${size}px ${alpha(theme.palette.secondary.main, 0.2)}`,
}));

const BubbleParticle = styled('div')(({ theme, size = 15, delay = 0, duration = 8 }) => ({
  position: 'absolute',
  width: size,
  height: size,
  borderRadius: '50%',
  background: `radial-gradient(circle at 30% 30%, ${alpha('#ffffff', 0.8)}, ${alpha(theme.palette.primary.light, 0.3)})`,
  animation: `${bubble} ${duration}s linear infinite`,
  animationDelay: `${delay}s`,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
}));

const EnergyOrb = styled('div')(({ theme, size = 60, delay = 0 }) => ({
  position: 'absolute',
  width: size,
  height: size,
  borderRadius: '50%',
  background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.1)}, transparent 70%)`,
  animation: `${pulse} 4s ease-in-out infinite`,
  animationDelay: `${delay}s`,
  border: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`,
}));

const AnimatedBackground = styled('div')({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  pointerEvents: 'none',
  zIndex: 0,
});

const FormPaper = styled(Paper)(({ theme }) => ({
  width: '100%',
  maxWidth: 450,
  padding: theme.spacing(4),
  borderRadius: theme.spacing(2),
  boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
  backdropFilter: 'blur(15px)',
  background: alpha(theme.palette.background.paper, 0.9),
  border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
  position: 'relative',
  zIndex: 10
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  marginBottom: theme.spacing(3),
  '& img': {
    width: '180px',
    height: 'auto',
    marginBottom: theme.spacing(1),
    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
  }
}));

const BrandTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  marginBottom: theme.spacing(1)
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.spacing(1.5),
    backgroundColor: alpha(theme.palette.background.paper, 0.8),
    backdropFilter: 'blur(10px)',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: alpha(theme.palette.background.paper, 0.9),
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: alpha(theme.palette.primary.main, 0.5),
      },
    },
    '&.Mui-focused': {
      backgroundColor: theme.palette.background.paper,
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.main,
        borderWidth: 2,
      },
    },
  },
  '& .MuiInputLabel-root': {
    fontWeight: 500,
  },
  marginBottom: theme.spacing(2.5)
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.spacing(1.5),
  padding: theme.spacing(1.5, 3),
  fontWeight: 600,
  textTransform: 'none',
  fontSize: '1rem',
  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
  transition: 'all 0.3s ease',
  '&:hover': {
    background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 20px rgba(0,0,0,0.2)',
  },
  '&:disabled': {
    background: theme.palette.action.disabledBackground,
    transform: 'none',
    boxShadow: 'none',
  }
}));

const PasswordStrengthBar = styled(LinearProgress)(({ theme }) => ({
  height: 4,
  borderRadius: 2,
  marginTop: theme.spacing(1),
  '& .MuiLinearProgress-bar': {
    borderRadius: 2,
  }
}));

const Footer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: theme.spacing(2),
  left: '50%',
  transform: 'translateX(-50%)',
  textAlign: 'center',
  zIndex: 10
}));

// Logo's
const dyconLogo = '/images/logo_dycon.svg';
const slimverbruikLogo = '/images/logo_slimverbruik.nl.svg';

const ResetPasswordPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [tokenValidating, setTokenValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [userEmail, setUserEmail] = useState('');

  // Validate token on component mount (with debounce to prevent double calls)
  useEffect(() => {
    let validationTimeout;
    
    const validateToken = async () => {
      if (!token) {
        setError('Ongeldige reset link. Vraag een nieuwe reset link aan.');
        setTokenValidating(false);
        return;
      }

      // Debounce om dubbele calls te voorkomen
      clearTimeout(validationTimeout);
      validationTimeout = setTimeout(async () => {
        try {
          setTokenValidating(true);
          const response = await fetch(`${getCentralBackendURL()}/api/v1/auth/local/validate-reset-token?token=${token}`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            }
          });

          if (response.ok) {
            const data = await response.json();
            setTokenValid(true);
            setUserEmail(data.user_email || '');
            console.log('Token validation successful:', data.message);
          } else {
            const errorData = await response.json();
            setTokenValid(false);
            
            if (response.status === 429) {
              setError('Te veel validatie pogingen. Probeer over 15 minuten opnieuw.');
            } else {
              setError(errorData.detail || 'Ongeldige of verlopen reset token');
            }
          }
        } catch (err) {
          console.error('Token validation error:', err);
          setTokenValid(false);
          setError('Kan geen verbinding maken met de server. Probeer opnieuw.');
        } finally {
          setTokenValidating(false);
        }
      }, 300); // 300ms debounce
    };

    validateToken();
    
    return () => {
      clearTimeout(validationTimeout);
    };
  }, [token]);

  // Password strength calculation
  const getPasswordStrength = (password) => {
    let score = 0;
    const requirements = [];

    if (password.length >= 8) {
      score += 20;
      requirements.push({ text: 'Min. 8 karakters', met: true });
    } else {
      requirements.push({ text: 'Min. 8 karakters', met: false });
    }

    if (/[a-z]/.test(password)) {
      score += 20;
      requirements.push({ text: 'Kleine letter', met: true });
    } else {
      requirements.push({ text: 'Kleine letter', met: false });
    }

    if (/[A-Z]/.test(password)) {
      score += 20;
      requirements.push({ text: 'Hoofdletter', met: true });
    } else {
      requirements.push({ text: 'Hoofdletter', met: false });
    }

    if (/[0-9]/.test(password)) {
      score += 20;
      requirements.push({ text: 'Cijfer', met: true });
    } else {
      requirements.push({ text: 'Cijfer', met: false });
    }

    if (/[^A-Za-z0-9]/.test(password)) {
      score += 20;
      requirements.push({ text: 'Speciaal teken', met: true });
    } else {
      requirements.push({ text: 'Speciaal teken', met: false });
    }

    return { score, requirements };
  };

  const passwordStrength = getPasswordStrength(formData.newPassword);

  const getStrengthColor = (score) => {
    if (score < 40) return 'error';
    if (score < 80) return 'warning';
    return 'success';
  };

  const getStrengthText = (score) => {
    if (score < 40) return 'Zwak';
    if (score < 80) return 'Gemiddeld';
    return 'Sterk';
  };

  const handleInputChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value
    });
    if (error) setError(null);
  };

  const validateForm = () => {
    if (!formData.newPassword) {
      setError('Nieuw wachtwoord is verplicht');
      return false;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError('Wachtwoorden komen niet overeen');
      return false;
    }

    if (passwordStrength.score < 80) {
      setError('Wachtwoord voldoet niet aan alle vereisten');
      return false;
    }

    return true;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!token) {
      setError('Geen geldig reset token gevonden');
      return;
    }

    if (!tokenValid) {
      setError('Token is niet geldig. Kan wachtwoord niet resetten.');
      return;
    }

    if (!validateForm()) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${getCentralBackendURL()}/api/v1/auth/local/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: token,
          new_password: formData.newPassword
        })
      });

      if (response.ok) {
        setSuccess(true);
      } else {
        const errorData = await response.json();
        if (errorData.detail) {
          if (typeof errorData.detail === 'object' && errorData.detail.errors) {
            setError(errorData.detail.errors.join(', '));
          } else {
            setError(errorData.detail);
          }
        } else {
          setError('Er ging iets mis bij het resetten van het wachtwoord');
        }
      }
    } catch (err) {
      console.error('Reset password error:', err);
      setError('Kan geen verbinding maken met de server. Probeer opnieuw.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <PageContainer maxWidth={false}>
        <AnimatedBackground>
          {/* Waterstof molecules - success variant */}
          <HydrogenMolecule style={{ top: '20%', left: '20%' }} size={40} delay={0} duration={8} />
          <HydrogenMolecule style={{ top: '30%', right: '25%' }} size={45} delay={1} duration={10} />
          <HydrogenMolecule style={{ bottom: '30%', left: '30%' }} size={35} delay={2} duration={9} />
          <HydrogenMolecule style={{ bottom: '25%', right: '20%' }} size={42} delay={3} duration={11} />
          
          {/* Floating particles */}
          <FloatingParticle style={{ top: '25%', left: '40%' }} size={20} delay={0} duration={13} />
          <FloatingParticle style={{ top: '35%', right: '40%' }} size={18} delay={1} duration={15} />
          <FloatingParticle style={{ bottom: '35%', left: '50%' }} size={22} delay={2} duration={12} />
          
          {/* Bubble particles */}
          <BubbleParticle style={{ left: '30%' }} size={14} delay={0} duration={7} />
          <BubbleParticle style={{ left: '55%' }} size={12} delay={1} duration={8} />
          <BubbleParticle style={{ left: '70%' }} size={16} delay={2} duration={6} />
          
          {/* Energy orbs */}
          <EnergyOrb style={{ top: '30%', left: '60%' }} size={75} delay={0} />
          <EnergyOrb style={{ bottom: '30%', right: '60%' }} size={85} delay={1} />
        </AnimatedBackground>

        <FormPaper elevation={0}>
          <LogoContainer>
            <img src={dyconLogo} alt="Dycon Logo" />
            <BrandTitle variant="h4" component="h1">
              Axioniq
            </BrandTitle>
          </LogoContainer>

          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600, color: 'success.main' }}>
              Wachtwoord Gewijzigd!
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Uw wachtwoord is succesvol gewijzigd. U kunt nu inloggen met uw nieuwe wachtwoord.
            </Typography>
          </Box>

          <Box sx={{ textAlign: 'center' }}>
            <StyledButton
              component={Link}
              to="/login"
              variant="contained"
              size="large"
            >
              Naar Inloggen
            </StyledButton>
          </Box>

          <Box sx={{ mt: 4, textAlign: 'center', pt: 2, borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Powered by
            </Typography>
            <img
              src={slimverbruikLogo}
              alt="Slimverbruik.nl"
              style={{ height: '24px', width: 'auto' }}
            />
          </Box>
        </FormPaper>

        <Footer>
          <Typography variant="body2" color="text.secondary">
            © 2025 Dycon · Innovatieve energieoplossingen
          </Typography>
        </Footer>
      </PageContainer>
    );
  }

  return (
    <PageContainer maxWidth={false}>
      <AnimatedBackground>
        {/* Waterstof molecules */}
        <HydrogenMolecule style={{ top: '10%', left: '10%' }} size={50} delay={0} duration={10} />
        <HydrogenMolecule style={{ top: '20%', right: '15%' }} size={35} delay={2} duration={8} />
        <HydrogenMolecule style={{ bottom: '30%', left: '20%' }} size={40} delay={4} duration={12} />
        <HydrogenMolecule style={{ bottom: '15%', right: '10%' }} size={45} delay={1} duration={9} />
        <HydrogenMolecule style={{ top: '50%', left: '5%' }} size={30} delay={3} duration={11} />
        <HydrogenMolecule style={{ top: '70%', right: '25%' }} size={38} delay={5} duration={7} />

        {/* Floating particles */}
        <FloatingParticle style={{ top: '15%', left: '30%' }} size={25} delay={0} duration={15} />
        <FloatingParticle style={{ top: '25%', right: '30%' }} size={20} delay={2} duration={12} />
        <FloatingParticle style={{ bottom: '25%', left: '40%' }} size={18} delay={4} duration={14} />
        <FloatingParticle style={{ bottom: '35%', right: '35%' }} size={22} delay={1} duration={13} />
        <FloatingParticle style={{ top: '60%', left: '15%' }} size={16} delay={3} duration={16} />
        <FloatingParticle style={{ top: '40%', right: '20%' }} size={24} delay={5} duration={11} />

        {/* Bubble particles (elektrolyse bubbles) */}
        <BubbleParticle style={{ left: '20%' }} size={12} delay={0} duration={6} />
        <BubbleParticle style={{ left: '40%' }} size={15} delay={1} duration={8} />
        <BubbleParticle style={{ left: '60%' }} size={10} delay={2} duration={7} />
        <BubbleParticle style={{ left: '80%' }} size={14} delay={3} duration={9} />
        <BubbleParticle style={{ left: '30%' }} size={11} delay={4} duration={6} />
        <BubbleParticle style={{ left: '70%' }} size={13} delay={5} duration={8} />

        {/* Energy orbs */}
        <EnergyOrb style={{ top: '20%', left: '50%' }} size={80} delay={0} />
        <EnergyOrb style={{ bottom: '20%', right: '50%' }} size={100} delay={2} />
        <EnergyOrb style={{ top: '60%', left: '70%' }} size={60} delay={1} />
      </AnimatedBackground>

      <FormPaper elevation={0}>
        <LogoContainer>
          <img src={dyconLogo} alt="Dycon Logo" />
          <BrandTitle variant="h4" component="h1">
            Axioniq
          </BrandTitle>
        </LogoContainer>

        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
            Nieuw Wachtwoord
          </Typography>
          {tokenValidating ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                Token wordt gevalideerd...
              </Typography>
            </Box>
          ) : tokenValid ? (
            <Box>
              <Typography variant="body2" color="success.main" sx={{ mb: 1 }}>
                ✓ Token is geldig
              </Typography>
              {userEmail && (
                <Typography variant="body2" color="text.secondary">
                  Nieuw wachtwoord instellen voor: {userEmail}
                </Typography>
              )}
            </Box>
          ) : (
            <Typography variant="body2" color="error.main">
              ✗ Token is ongeldig of verlopen
            </Typography>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 1.5 }}>
            {error}
          </Alert>
        )}

        {!tokenValidating && tokenValid && (
          <Box component="form" onSubmit={handleSubmit}>
          <StyledTextField
            fullWidth
            type={showPassword ? 'text' : 'password'}
            label="Nieuw wachtwoord"
            value={formData.newPassword}
            onChange={handleInputChange('newPassword')}
            disabled={loading}
            required
            autoComplete="new-password"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock color="action" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {formData.newPassword && (
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Security fontSize="small" color="action" sx={{ mr: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  Wachtwoordsterkte: {getStrengthText(passwordStrength.score)}
                </Typography>
              </Box>
              <PasswordStrengthBar 
                variant="determinate" 
                value={passwordStrength.score} 
                color={getStrengthColor(passwordStrength.score)}
              />
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                {passwordStrength.requirements.map((req, index) => (
                  <Chip
                    key={index}
                    size="small"
                    icon={req.met ? <CheckCircle /> : <Cancel />}
                    label={req.text}
                    color={req.met ? 'success' : 'default'}
                    variant={req.met ? 'filled' : 'outlined'}
                    sx={{ fontSize: '0.75rem' }}
                  />
                ))}
              </Stack>
            </Box>
          )}

          <StyledTextField
            fullWidth
            type={showConfirmPassword ? 'text' : 'password'}
            label="Bevestig nieuw wachtwoord"
            value={formData.confirmPassword}
            onChange={handleInputChange('confirmPassword')}
            disabled={loading}
            required
            autoComplete="new-password"
            error={formData.confirmPassword && formData.newPassword !== formData.confirmPassword}
            helperText={formData.confirmPassword && formData.newPassword !== formData.confirmPassword ? 'Wachtwoorden komen niet overeen' : ''}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock color="action" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <StyledButton
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={loading || !token || !tokenValid}
            sx={{ mb: 2 }}
          >
            {loading ? (
              <CircularProgress size={24} sx={{ color: 'white' }} />
            ) : (
              'Wachtwoord Wijzigen'
            )}
          </StyledButton>
        </Box>
        )}

        <Box sx={{ textAlign: 'center' }}>
          <MuiLink
            component={Link}
            to="/login"
            variant="body2"
            sx={{ 
              color: 'text.secondary',
              textDecoration: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '&:hover': {
                textDecoration: 'underline',
                color: 'primary.main'
              }
            }}
          >
            <ArrowBack sx={{ mr: 1, fontSize: 16 }} />
            Terug naar inloggen
          </MuiLink>
        </Box>

        <Box sx={{ mt: 4, textAlign: 'center', pt: 2, borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Powered by
          </Typography>
          <img
            src={slimverbruikLogo}
            alt="Slimverbruik.nl"
            style={{ height: '24px', width: 'auto' }}
          />
        </Box>
      </FormPaper>

      <Footer>
        <Typography variant="body2" color="text.secondary">
          © 2025 Dycon · Innovatieve energieoplossingen
        </Typography>
      </Footer>
    </PageContainer>
  );
};

export default ResetPasswordPage; 